import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { actionBack } from '@ibk/shared-assets';
import { HTTP_STATUS } from '@ibk/shared-config';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { Alert, Button, Icon, Loading } from '@ibk/shared-ui';
import { ApiErrors } from '../../enums';
import { useBenefitContractLocation } from '../../hooks/useBenefitContractLocation';
import { beneficiosTranslations } from '../../translations';
import { AddressListItem } from '../AddressListItem';
import { AddressModal } from '../AddressModal';
import styles from './AddressListModal.module.css';
function AddressListModalContent({ show, title, onHide, onChange, onSelectAddress, isSimulating = false, isHiring = false, testIdIdentifier = 'AddressListModal', }) {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [address, setAddress] = useState(null);
    const [alert, setAlert] = useState(null);
    const { locations, isLoading: isLoadingDeliveryLocations, isError: isErrorDeliveryLocations, createDeliveryLocation, updateDeliveryLocation, isCreating, isUpdating, } = useBenefitContractLocation();
    const isLoading = isLoadingDeliveryLocations || isSimulating;
    useEffect(() => {
        if (!show) {
            setAlert(null);
            setAddress(null);
            setShowModal(false);
        }
    }, [show]);
    const handleSave = async (newLocation) => {
        setAlert(null);
        const { deliveryLocationId, locationId, name, ...body } = newLocation;
        if (deliveryLocationId) {
            await updateDeliveryLocation(deliveryLocationId, { ...body, name: name ?? '' });
            setAlert({ message: t('address.success.update'), type: 'success' });
        }
        else {
            await createDeliveryLocation({ ...body, name: name ?? '' });
            setAlert({ message: t('address.success.create'), type: 'success' });
        }
        onChange?.();
    };
    const handleAddNewAddress = () => {
        setAddress(null);
        setShowModal(true);
    };
    const handleActivateAddress = async () => {
        try {
            if (onSelectAddress) {
                await onSelectAddress();
            }
            await new Promise(resolve => setTimeout(resolve, 100));
            setAlert({ message: t('address.success.activate'), type: 'success' });
            onChange?.();
        }
        catch {
            setAlert({ message: t('address.error.activate'), type: 'error' });
        }
    };
    const handleActivateError = () => {
        setAlert({ message: t('address.error.activate'), type: 'error' });
    };
    const handleDelete = () => {
        setAlert({ message: t('address.success.delete'), type: 'success' });
        onChange?.();
    };
    const handleDeleteError = (error) => {
        const isAssociatedError = isAssociatedWithBeneficiaryError(error);
        setAlert({
            message: isAssociatedError ? t('address.error.delete.associatedWithBeneficiary') : t('address.error.delete'),
            type: 'error',
        });
    };
    const handleClickUpdate = (updatedAddress) => {
        setAddress(updatedAddress);
        setShowModal(true);
    };
    const renderLoading = () => {
        return (_jsxs("div", { className: styles.loadingContainer, "data-testid": `view${testIdIdentifier}LoadingContainer`, children: [_jsx(Loading, { size: 'small' }), isSimulating && (_jsx("p", { className: styles.loadingText, "data-testid": `text${testIdIdentifier}LoadingText`, children: t('address.simulating') }))] }));
    };
    const isAssociatedWithBeneficiaryError = (error) => {
        const errorObj = error;
        const errorStatus = errorObj?.status ?? errorObj?.details?.status;
        const errorDetailsResult = errorObj?.details?.result;
        return (errorStatus === HTTP_STATUS.BAD_REQUEST &&
            errorDetailsResult === ApiErrors.DELIVERY_LOCATION_ASSOCIATED_WITH_BENEFICIARY_CANNOT_BE_DELETED);
    };
    const renderError = () => {
        return (_jsxs("div", { className: styles.errorContainer, "data-testid": `view${testIdIdentifier}ErrorContainer`, children: [_jsx("div", { className: styles.errorIcon, children: _jsx(Icon, { name: 'location' }) }), _jsx("h3", { className: styles.errorTitle, "data-testid": `text${testIdIdentifier}ErrorTitle`, children: t('address.error.title') }), _jsx("p", { className: styles.errorDescription, "data-testid": `text${testIdIdentifier}ErrorDescription`, children: t('address.error.description') })] }));
    };
    const renderAddressList = () => {
        return (_jsx(_Fragment, { children: locations.length > 0 &&
                locations.map((location, index) => (_jsx(AddressListItem, { address: location, onDelete: handleDelete, onDeleteError: handleDeleteError, onActivate: handleActivateAddress, onActivateError: handleActivateError, onClickUpdate: handleClickUpdate, testIdIdentifier: `view${testIdIdentifier}AddressListItem${index}` }, location.deliveryLocationId))) }));
    };
    const renderFooter = () => {
        return (_jsxs(Modal.Footer, { className: styles.modalFooter, "data-testid": `modal${testIdIdentifier}Footer`, children: [_jsx(Button, { variant: 'tertiary', size: 'medium', onClick: onHide, dataTestId: `button${testIdIdentifier}Edit`, children: t('back') }), _jsx(Button, { variant: 'secondary', size: 'medium', onClick: handleAddNewAddress, disabled: isLoadingDeliveryLocations, leftIcon: 'actionAdd', dataTestId: `button${testIdIdentifier}AddNewAddress`, children: t('address.button.addNewAddress') })] }));
    };
    return (_jsxs(Modal, { show: show, onHide: onHide, dialogClassName: styles.modalDialog, contentClassName: styles.modalContent, className: styles.modal, backdropClassName: styles.backdrop, "data-testid": `modal${testIdIdentifier}Container`, children: [_jsxs(Modal.Header, { className: styles.modalHeader, "data-testid": `modal${testIdIdentifier}Header`, children: [_jsx("button", { type: 'button', onClick: onHide, className: styles.backButton, "data-testid": `button${testIdIdentifier}Back`, children: _jsx("img", { src: actionBack, alt: '' }) }), _jsx(Modal.Title, { className: styles.modalTitle, "data-testid": `text${testIdIdentifier}Title`, children: title })] }), _jsxs(Modal.Body, { className: styles.modalBody, "data-testid": `modal${testIdIdentifier}Body`, children: [alert?.message && (_jsx(Alert, { alertType: 'inline', appearance: alert.type, density: 'standard', alignItems: 'center', message: alert.message, onClose: () => setAlert({ message: '', type: 'success' }), className: styles.alert, "data-testid": `alert${testIdIdentifier}Alert` })), isLoading && renderLoading(), !isLoading && !isErrorDeliveryLocations && renderAddressList(), !isLoading && isErrorDeliveryLocations && renderError()] }), !isLoading && !isErrorDeliveryLocations && renderFooter(), _jsx(AddressModal, { show: showModal, title: title, onHide: () => setShowModal(false), onSave: handleSave, isSaving: isCreating || isUpdating, isHiring: isHiring, currentAddress: address ?? undefined, testIdIdentifier: `modal${testIdIdentifier}AddressModal` })] }));
}
export function AddressListModal(props) {
    return (_jsx(TranslationProvider, { translations: beneficiosTranslations, children: _jsx(AddressListModalContent, { ...props }) }));
}
